import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { api, formatCurrency, formatDate } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Plus, FileDown, MoreHorizontal, Eye, Trash2, FileSignature, Mail, Loader2, BarChart3, MapPin, Users2, ClipboardCheck, Send, Pencil, ShieldCheck } from 'lucide-react';
import { SendContractEmailDialog } from '@/components/SendContractEmailDialog';
import { PdfPreviewDialog } from '@/components/PdfPreviewDialog';
import { useAuth, isAdmin, isPartner } from '@/contexts/AuthContext';

const statusVariant = {
  bozza: 'outline', compilato: 'secondary', firmato: 'default', inviato: 'default', annullato: 'destructive'
};

export default function Contracts() {
  const { user } = useAuth();
  const canManage = isAdmin(user) || isPartner(user);
  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [aggregations, setAggregations] = useState(null);
  const [showAggregations, setShowAggregations] = useState(false);
  const [filterCompany, setFilterCompany] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [emailContract, setEmailContract] = useState(null);
  const [pdfPreview, setPdfPreview] = useState({ open: false, url: null, loading: false, title: '' });
  // Stato di generazione PDF per singola riga (evita doppi click e mostra spinner)
  const [generatingPdf, setGeneratingPdf] = useState({}); // { [contractId+mode]: true }
  const isGenerating = (id, mode) => !!generatingPdf[`${id}:${mode}`];
  const generatePdf = async (c, mode) => {
    const key = `${c.id}:${mode}`;
    if (generatingPdf[key]) return;
    setGeneratingPdf(prev => ({ ...prev, [key]: true }));
    const labelMap = { blank: 'PDF contratto stampabile vuoto', filled: 'PDF compilato', signed: 'PDF firmato' };
    const tId = toast.loading(`Generazione ${labelMap[mode] || 'PDF'} in corso, attendere…`);
    try {
      await api.openContractPdf(c.id, mode);
      toast.success('PDF generato', { id: tId });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore generazione PDF', { id: tId });
    } finally {
      setGeneratingPdf(prev => { const n = { ...prev }; delete n[key]; return n; });
    }
  };

  const openPdfPreview = async (c) => {
    const mode = c?.signature?.customer_signature_data_url ? 'signed' : 'filled';
    setPdfPreview({ open: true, url: null, loading: true, title: `Contratto ${c?.contract?.number || ''}`.trim() });
    try {
      const url = await api.fetchContractPdfUrl(c.id, mode);
      setPdfPreview(p => ({ ...p, url, loading: false }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore generazione anteprima');
      setPdfPreview({ open: false, url: null, loading: false, title: '' });
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [c, comps] = await Promise.all([api.listContracts(), api.listCompanies()]);
      setItems(c); setCompanies(comps);
    } catch (e) { toast.error('Errore caricamento'); }
    setLoading(false);
    // Aggregations (solo admin/partner)
    if (canManage) {
      try {
        const agg = await api.contractsAggregations();
        setAggregations(agg);
      } catch { /* silent */ }
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const handleDelete = async (id) => { try { await api.deleteContract(id); toast.success('Contratto eliminato'); load(); } catch { toast.error('Errore'); } };
  const handleSetStatus = async (c, newStatus, label) => {
    if (c.status === newStatus) { toast.info(`Il contratto è già in stato "${label}"`); return; }
    try {
      await api.updateContract(c.id, { status: newStatus });
      toast.success(`Stato aggiornato: ${label}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore aggiornamento stato');
    }
  };
  const handleManualSign = async (c) => {
    if (c.status === 'firmato') { toast.info('Il contratto è già firmato'); return; }
    if (!window.confirm('Confermi la marcatura come "firmato manualmente"?\nLo stato passerà a Firmato e la modalità firma verrà registrata come manuale.')) return;
    try {
      const nowIso = new Date().toISOString();
      await api.updateContract(c.id, {
        status: 'firmato',
        signature: {
          ...(c.signature || {}),
          signature_mode: 'manual',
          signed_at: nowIso,
          signed_place: (c.signature?.signed_place) || (c.contract?.place) || '',
        },
      });
      toast.success('Contratto marcato come firmato manualmente');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore firma manuale');
    }
  };

  const companiesMap = useMemo(() => Object.fromEntries(companies.map(c => [c.id, c])), [companies]);

  const filtered = items.filter(c => {
    if (filterCompany !== 'all' && c.company_id !== filterCompany) return false;
    if (filterStatus !== 'all' && c.status !== filterStatus) return false;
    if (search) {
      const t = search.toLowerCase();
      const hay = [c.contract?.number, c.customer?.first_name, c.customer?.last_name, c.customer?.fiscal_code].filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(t)) return false;
    }
    return true;
  });

  return (
    <div>
      <PageHeader
        title="Contratti"
        description="Storico dei contratti generati, con download PDF e gestione stati"
        actions={
          <div className="flex gap-2">
            {canManage && aggregations && (
              <Button variant="outline" onClick={() => setShowAggregations(v => !v)} data-testid="contracts-toggle-aggregations">
                <BarChart3 className="size-4 mr-2" />{showAggregations ? 'Nascondi report' : 'Mostra report'}
              </Button>
            )}
            <Button asChild data-testid="contracts-new-button"><Link to="/contracts/new"><Plus className="size-4 mr-2" />Nuovo contratto</Link></Button>
          </div>
        }
      />

      {/* Dashboard aggregata (solo admin / partner BO) */}
      {canManage && showAggregations && aggregations && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4" data-testid="contracts-aggregations">
          <Card>
            <CardContent className="pt-5">
              <div className="text-xs text-muted-foreground flex items-center gap-1"><ClipboardCheck className="size-3.5" />Totale contratti</div>
              <div className="text-2xl font-semibold tabular-nums mt-1" data-testid="agg-total-count">{aggregations.total_count}</div>
              <div className="text-sm text-muted-foreground tabular-nums mt-0.5">{formatCurrency(aggregations.total_amount)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5">
              <div className="text-xs text-muted-foreground flex items-center gap-1"><ShieldCheck className="size-3.5" />Per stato</div>
              <div className="space-y-1 mt-1">
                {(aggregations.by_status || []).slice(0, 5).map(s => (
                  <div key={s.key} className="flex items-center justify-between text-xs">
                    <span className="capitalize">{s.key}</span>
                    <span className="tabular-nums font-medium">{s.count} · {formatCurrency(s.amount)}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5">
              <div className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="size-3.5" />Top località</div>
              <div className="space-y-1 mt-1">
                {(aggregations.by_city || []).slice(0, 5).map(s => (
                  <div key={s.key} className="flex items-center justify-between text-xs">
                    <span className="truncate max-w-[120px]" title={s.key}>{s.key}</span>
                    <span className="tabular-nums font-medium">{s.count} · {formatCurrency(s.amount)}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5">
              <div className="text-xs text-muted-foreground flex items-center gap-1"><Users2 className="size-3.5" />Top agenti</div>
              <div className="space-y-1 mt-1">
                {(aggregations.by_agent || []).slice(0, 5).map(s => (
                  <div key={s.key} className="flex items-center justify-between text-xs">
                    <span className="truncate max-w-[120px]" title={s.name}>{s.name}</span>
                    <span className="tabular-nums font-medium">{s.count} · {formatCurrency(s.amount)}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          <div className="p-4 border-b flex flex-col md:flex-row md:items-center gap-3">
            <Input placeholder="Cerca numero, cliente, CF..." value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-sm" data-testid="contracts-search-input" />
            <Select value={filterCompany} onValueChange={setFilterCompany}>
              <SelectTrigger className="w-full md:w-[200px]" data-testid="contracts-filter-company"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le aziende</SelectItem>
                {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full md:w-[170px]" data-testid="contracts-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli stati</SelectItem>
                <SelectItem value="bozza">Bozza</SelectItem>
                <SelectItem value="compilato">Compilato</SelectItem>
                <SelectItem value="firmato">Firmato</SelectItem>
                <SelectItem value="inviato">Inviato</SelectItem>
                <SelectItem value="annullato">Annullato</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex-1" />
            <div className="text-xs text-muted-foreground">{filtered.length} contratti</div>
          </div>

          {filtered.length === 0 ? (
            <div className="p-12 text-center">
              <div className="mx-auto size-12 rounded-full bg-secondary grid place-items-center mb-3"><FileSignature className="size-5 text-muted-foreground" /></div>
              <div className="text-sm font-medium">{loading ? 'Caricamento...' : 'Nessun contratto'}</div>
              {!loading && <div className="text-xs text-muted-foreground mb-4">Inizia creando il primo contratto</div>}
              {!loading && <Button asChild size="sm"><Link to="/contracts/new"><Plus className="size-4 mr-2" />Nuovo contratto</Link></Button>}
            </div>
          ) : (
            <Table data-testid="contracts-table">
              <TableHeader><TableRow>
                <TableHead>Numero</TableHead><TableHead>Cliente</TableHead><TableHead>Partner</TableHead>
                <TableHead>Stato</TableHead><TableHead>Data</TableHead>
                <TableHead className="text-right">Totale</TableHead><TableHead className="text-right">Azioni</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {filtered.map(c => (
                  <TableRow key={c.id} data-testid={`contract-row-${c.id}`}>
                    <TableCell><Link to={`/contracts/${c.id}`} className="font-mono text-sm hover:underline">{c.contract?.number}</Link></TableCell>
                    <TableCell><div className="font-medium">{c.customer?.first_name} {c.customer?.last_name}</div><div className="text-xs text-muted-foreground font-mono">{c.customer?.fiscal_code}</div></TableCell>
                    <TableCell className="text-sm">{companiesMap[c.company_id]?.name || '—'}</TableCell>
                    <TableCell><Badge variant={statusVariant[c.status] || 'outline'} className="capitalize">{c.status}</Badge></TableCell>
                    <TableCell className="text-sm">{formatDate(c.contract?.date)}</TableCell>
                    <TableCell className="text-right tabular-nums font-medium">{formatCurrency(c.totals?.total)}</TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`contract-actions-${c.id}`}><MoreHorizontal className="size-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                          <DropdownMenuItem asChild><Link to={`/contracts/${c.id}`}><Eye className="size-4 mr-2" />Apri / Modifica</Link></DropdownMenuItem>
                          {canManage && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuLabel className="text-xs">Stato contratto</DropdownMenuLabel>
                              {c.status !== 'inviato' && (
                                <DropdownMenuItem onClick={() => handleSetStatus(c, 'inviato', 'In attesa firma')} data-testid={`contract-set-inviato-${c.id}`}>
                                  <Send className="size-4 mr-2" />Converti in attesa firma
                                </DropdownMenuItem>
                              )}
                              {c.status !== 'firmato' && (
                                <DropdownMenuItem onClick={() => handleManualSign(c)} data-testid={`contract-manual-sign-${c.id}`}>
                                  <FileSignature className="size-4 mr-2" />Firmato manualmente
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem asChild data-testid={`contract-resume-otp-${c.id}`}>
                                <Link to={`/contracts/${c.id}#otp`}><ShieldCheck className="size-4 mr-2" />Riprendi firma OTP</Link>
                              </DropdownMenuItem>
                            </>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuLabel className="text-xs">Scarica PDF</DropdownMenuLabel>
                          <DropdownMenuItem onSelect={(e) => e.preventDefault()} disabled={isGenerating(c.id, 'blank')} onClick={() => generatePdf(c, 'blank')} data-testid={`pdf-download-blank-${c.id}`}>
                            {isGenerating(c.id, 'blank') ? <Loader2 className="size-4 mr-2 animate-spin" /> : <FileDown className="size-4 mr-2" />}
                            {isGenerating(c.id, 'blank') ? 'Generazione in corso…' : 'PDF Contratto Stampabile Vuoto'}
                          </DropdownMenuItem>
                          <DropdownMenuItem onSelect={(e) => e.preventDefault()} disabled={isGenerating(c.id, 'filled')} onClick={() => generatePdf(c, 'filled')} data-testid={`pdf-download-filled-${c.id}`}>
                            {isGenerating(c.id, 'filled') ? <Loader2 className="size-4 mr-2 animate-spin" /> : <FileDown className="size-4 mr-2" />}
                            {isGenerating(c.id, 'filled') ? 'Generazione in corso…' : 'Compilato'}
                          </DropdownMenuItem>
                          <DropdownMenuItem onSelect={(e) => e.preventDefault()} disabled={isGenerating(c.id, 'signed')} onClick={() => generatePdf(c, 'signed')} data-testid={`pdf-download-signed-${c.id}`}>
                            {isGenerating(c.id, 'signed') ? <Loader2 className="size-4 mr-2 animate-spin" /> : <FileDown className="size-4 mr-2" />}
                            {isGenerating(c.id, 'signed') ? 'Generazione in corso…' : 'Firmato'}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => openPdfPreview(c)} data-testid={`contract-pdf-preview-${c.id}`}><Eye className="size-4 mr-2" />Anteprima PDF</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setEmailContract(c)} data-testid={`contract-send-email-${c.id}`}><Mail className="size-4 mr-2" />Invia via email</DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <AlertDialog>
                            <AlertDialogTrigger asChild><DropdownMenuItem onSelect={(e) => e.preventDefault()} className="text-destructive"><Trash2 className="size-4 mr-2" />Elimina</DropdownMenuItem></AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader><AlertDialogTitle>Eliminare il contratto?</AlertDialogTitle><AlertDialogDescription>Azione irreversibile.</AlertDialogDescription></AlertDialogHeader>
                              <AlertDialogFooter><AlertDialogCancel>Annulla</AlertDialogCancel><AlertDialogAction onClick={() => handleDelete(c.id)}>Elimina</AlertDialogAction></AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <SendContractEmailDialog
        open={!!emailContract}
        onOpenChange={(v) => { if (!v) setEmailContract(null); }}
        contract={emailContract}
      />
      <PdfPreviewDialog
        open={pdfPreview.open}
        onOpenChange={(v) => { if (!v) setPdfPreview({ open: false, url: null, loading: false, title: '' }); }}
        title={pdfPreview.title}
        pdfUrl={pdfPreview.url}
        loading={pdfPreview.loading}
        filename={`${pdfPreview.title.replace(/\s+/g, '_') || 'contratto'}.pdf`}
      />
    </div>
  );
}
